import * as React from 'react';
import { Button, Card, Col, Divider, Modal, notification, Row, Table } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { withRouter, Link, RouteComponentProps } from 'react-router-dom';

import PaymentMethodsQuery from '../../components/Query/PaymentMethods';
import PaymentMethodDetail from './PaymentMethodDetail';
import PaymentGatewayTypes from '../../helpers/PaymentGateways/paymentGateways';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';
import PriceDisplay from '../../components/Others/PriceDisplay';

interface IPaymentMethodsTableProps extends RouteComponentProps {
  domain: string;
  paymentMethodId?: string;
  currency: GQL.ICurrency;
}

interface IPaymentMethodsTableState {
  paymentMethod?: GQL.IPaymentMethod;
}

class PaymentMethodsTable extends React.Component<IPaymentMethodsTableProps, IPaymentMethodsTableState> {

  constructor(props: IPaymentMethodsTableProps) {
    super(props);
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <PaymentMethodsQuery
            domain={this.props.domain}
          >
            {({ paymentMethods, createPaymentMethod, updatePaymentMethod, deletePaymentMethod }: any) => {
              return this.renderData(
                paymentMethods,
                createPaymentMethod,
                updatePaymentMethod,
                deletePaymentMethod,
                paymentMethods.refetch
              );
            }}
          </PaymentMethodsQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    paymentMethods: any,
    createPaymentMethod: Function,
    updatePaymentMethod: Function,
    deletePaymentMethod: Function,
    refetch: Function
  ) {
    const { currency } = this.props;
    
    if (paymentMethods && !paymentMethods.loading) {

      // formulář
      if (this.props && this.props.paymentMethodId) {
        const selectedDeliveryMethod = paymentMethods.paymentMethods.find((s: GQL.IPaymentMethod) => s.id === this.props.paymentMethodId) || undefined;
        if (this.props.paymentMethodId !== 'new' && !selectedDeliveryMethod) {
          return <NotFoundPage domain={this.props.domain} />
        }
        return (
          <PaymentMethodDetail
            domain={this.props.domain}
            languages={paymentMethods.languages}
            paymentMethod={paymentMethods.paymentMethods.find((s: GQL.IPaymentMethod) => s.id === this.props.paymentMethodId) || {}}
            onSave={a => this.upsert(a, updatePaymentMethod, createPaymentMethod, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      return (
        <div>
          <Title>Payment methods</Title>
          <Divider />
          <Link className="ant-btn" to={this.getLink('new')}>Create new payment method</Link>
          <Table
            rowKey={(record: any) => record.id}
            dataSource={paymentMethods.paymentMethods || []}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column title="Title" dataIndex="title" key="title" />
            <Table.Column
              title="Gateway"
              dataIndex="gateway"
              key="gateway"
              width={180}
              render={(value: any) => PaymentGatewayTypes[value] || ''}
            />
            <Table.Column
              title="Price"
              dataIndex="price"
              key="price"
              align="right"
              render={(u, row: GQL.IPaymentMethod) => {
                const prices = row.prices 
                  ? row.prices.map((price: any) => {
                      return {
                        currencyId: price.currencyId,
                        priceWithout: price.priceWithout,
                      };
                    }) 
                  : [];

                return <PriceDisplay prices={prices} currency={currency} />;
              }}
            />
            <Table.Column
              title="Priority"
              dataIndex="priority"
              key="priority"
              align="center"
              width={90}
            />
            <Table.Column
              title="Active"
              dataIndex="active"
              key="active"
              width={100}
              align="center"
              render={(u, row: GQL.IPaymentMethod) => {
                return (
                  <span>
                    {row.active ? (
                      <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    ) : (
                        <CloseOutlined style={{ fontSize: 20 }} />
                      )}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={180}
              align="center"
              render={(u: any, row: any) => this.renderActions(u, row, deletePaymentMethod, refetch)}
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading payment methods ..." style={{ width: '100%' }} />
      </div>
    );
  }

  // @ts-ignore
  private renderActions(u: any, paymentMethod: GQL.IPaymentMethod, deletePaymentMethod: Function, refetch: Function) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink(paymentMethod.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button danger={true} onClick={() => this.delete(paymentMethod, deletePaymentMethod, refetch)}>
          Delete
        </Button>
      </div>
    );
  }

  private delete(paymentMethod: GQL.IPaymentMethod, deletePaymentMethod: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${paymentMethod.title}?`,
      content: (
        <span>
          Are you sure you want to delete payment method <strong>{paymentMethod.title}</strong>?
        </span>
      ),
      onOk: () => {
        return deletePaymentMethod({
          variables: {
            id: paymentMethod.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    paymentMethod: GQL.IPaymentMethod,
    updatePaymentMethod: Function,
    createPaymentMethod: Function,
    refetch: Function
  ) {
    if (paymentMethod !== undefined) {
      const variables = {
        id: paymentMethod.id,
        paymentMethod: {
          title: paymentMethod.title,
          name: paymentMethod.name,
          description: paymentMethod.description,
          price: paymentMethod.price || 0,
          prices: paymentMethod.prices && paymentMethod.prices.map((price: GQL.IPaymentMethodPrice) => {
            return {
              currencyId: price.currencyId,
              priceWithout: price.priceWithout,
            };
          }),
          image: paymentMethod.image,
          gateway: paymentMethod.gateway,
          priority: paymentMethod.priority,
          active: paymentMethod.active,
          countryIds: (paymentMethod as any).countryIds || [],
        }
      };

      if (paymentMethod.id) {
        return updatePaymentMethod({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Payment method "${variables.paymentMethod.title}" has been updated.`
            })
          );
      }

      (variables.paymentMethod as any).domain = this.props.domain;

      createPaymentMethod({ variables })
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Payment method "${variables.paymentMethod.title}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null) {
    return `/${this.props.domain}/application/paymentMethods${recordId ? `/${recordId}` : ''}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter(PaymentMethodsTable);



// WEBPACK FOOTER //
// ./src/scenes/PaymentMethods/PaymentMethodsTable.tsx